import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  customersApi, deliveryAreasApi, emptyCustomer, ordersApi, ORDER_STATUS_LABEL,
  ledgerApi, INVOICE_STATUS_LABEL,
  type CustomerInput, type DeliveryArea, type OrderListItem, type CustomerLedger, type PaymentType, type TriState,
} from '../crmApi'
import { apiErrorMessage, getMe } from '../api'
import { Tabs, type TabDef } from '../components/Tabs'
import { useDialogs } from '../components/Dialogs'

export function CustomerEdit() {
  const { confirm } = useDialogs()
  const { id } = useParams()
  const isNew = id === undefined
  const customerId = isNew ? undefined : Number(id)
  const navigate = useNavigate()

  const [areas, setAreas] = useState<DeliveryArea[]>([])
  const [form, setForm] = useState<CustomerInput | null>(null)
  const [accessKey, setAccessKey] = useState<string | null>(null)
  const [phoneVerifiedUtc, setPhoneVerifiedUtc] = useState<string | null>(null)
  const [phoneChangedUtc, setPhoneChangedUtc] = useState<string | null>(null)
  const [emailVerifiedUtc, setEmailVerifiedUtc] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [orders, setOrders] = useState<OrderListItem[]>([])
  const [ledger, setLedger] = useState<CustomerLedger | null>(null)
  const [pay, setPay] = useState<{ type: PaymentType; amount: string; date: string; invoiceId: string; comment: string }>(
    { type: 'ManualPayment', amount: '', date: new Date().toISOString().slice(0, 10), invoiceId: '', comment: '' })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    deliveryAreasApi.list().then(setAreas).catch((e) => setError(describe(e)))
    getMe().then((me) => setIsAdmin(me.roles.includes('HemaggAdmin'))).catch(() => { /* non-fatal */ })
    if (isNew) {
      setForm(emptyCustomer())
    } else {
      customersApi.get(customerId!)
        .then((c) => {
          setAccessKey(c.accessKey)
          setPhoneVerifiedUtc(c.mobilePhoneVerifiedUtc)
          setPhoneChangedUtc(c.mobilePhoneChangedUtc)
          setEmailVerifiedUtc(c.emailVerifiedUtc)
          const { id: _id, deliveryAreaName: _n, accessKey: _k,
            mobilePhoneVerifiedUtc: _v, mobilePhoneChangedUtc: _c, emailVerifiedUtc: _e, ...input } = c
          setForm(input)
        })
        .catch((e) => setError(describe(e)))
      ordersApi.list({ customerId: customerId! }).then(setOrders).catch(() => { /* non-fatal */ })
      void reloadLedger()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function reloadLedger() {
    if (isNew) return
    try { setLedger(await ledgerApi.get(customerId!)) } catch { /* non-fatal */ }
  }

  async function registerPayment() {
    if (isNew) return
    const amount = Number(pay.amount)
    if (!amount) { setError('Ange belopp.'); return }
    try {
      await ledgerApi.addPayment(customerId!, {
        type: pay.type, amount, date: pay.date,
        invoiceId: pay.invoiceId ? Number(pay.invoiceId) : null,
        comment: pay.comment || null,
      })
      setPay({ ...pay, amount: '', invoiceId: '', comment: '' })
      await reloadLedger()
    } catch (e) { setError(describe(e)) }
  }

  function set<K extends keyof CustomerInput>(key: K, value: CustomerInput[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f))
  }

  async function save() {
    if (!form) return
    if (!form.deliveryAreaId) { setError('Välj ett leveransområde.'); return }
    setSaving(true)
    try {
      const saved = isNew ? await customersApi.create(form) : await customersApi.update(customerId!, form)
      navigate(`/customers/${saved.id}`)
    } catch (e) {
      setError(describe(e))
    } finally {
      setSaving(false)
    }
  }

  async function resetPhoneVerification(clearNumber: boolean) {
    if (isNew) return
    const msg = clearNumber
      ? 'Rensa mobilnumret och återställ verifieringen? Kunden måste då ange ett nytt nummer på Mina uppgifter.'
      : 'Återställa verifieringen av mobilnumret? Kunden kan verifiera ett nummer på nytt direkt.'
    if (!(await confirm(msg, { confirmLabel: clearNumber ? 'Rensa & återställ' : 'Återställ' }))) return
    try {
      await customersApi.resetPhoneVerification(customerId!, clearNumber)
      setPhoneVerifiedUtc(null)
      setPhoneChangedUtc(null)
      if (clearNumber) set('mobilePhone', '')
    } catch (e) { setError(describe(e)) }
  }

  async function resetEmailVerification() {
    if (isNew) return
    if (!(await confirm('Återställa verifieringen av e-postadressen? Kunden kan verifiera den på nytt.',
      { confirmLabel: 'Återställ' }))) return
    try {
      await customersApi.resetEmailVerification(customerId!)
      setEmailVerifiedUtc(null)
    } catch (e) { setError(describe(e)) }
  }

  async function remove() {
    if (isNew) return
    if (!(await confirm('Radera kunden?', { danger: true, confirmLabel: 'Radera' }))) return
    try {
      await customersApi.remove(customerId!)
      navigate('/customers')
    } catch (e) {
      setError(describe(e))
    }
  }

  if (!form) return <p className="muted">{error ?? 'Laddar…'}</p>

  const detailsTab = (
    <>
      <div className="card form-grid">
        <Field label="Leveransområde *">
          <select value={form.deliveryAreaId} onChange={(e) => set('deliveryAreaId', Number(e.target.value))}>
            <option value={0}>— välj —</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        <Field label="Leveransordning">
          <input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', Number(e.target.value))} />
        </Field>

        <Field label="Förnamn *"><input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></Field>
        <Field label="Efternamn"><input value={form.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} /></Field>

        <Field label="Hemnummer"><input value={form.homePhone ?? ''} onChange={(e) => set('homePhone', e.target.value)} /></Field>
        <Field label="Mobilnummer"><input value={form.mobilePhone ?? ''} onChange={(e) => set('mobilePhone', e.target.value)} /></Field>
        <Field label="Jobbnummer"><input value={form.workPhone ?? ''} onChange={(e) => set('workPhone', e.target.value)} /></Field>
        <Field label="E-post"><input value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} /></Field>

        <Field label="Leveransadress *"><input value={form.deliveryStreet} onChange={(e) => set('deliveryStreet', e.target.value)} /></Field>
        <Field label="Postnummer"><input value={form.deliveryPostalCode ?? ''} onChange={(e) => set('deliveryPostalCode', e.target.value)} /></Field>
        <Field label="Ort"><input value={form.deliveryCity ?? ''} onChange={(e) => set('deliveryCity', e.target.value)} /></Field>
        <Field label="Kontakta ej förrän">
          <input type="date" value={form.doNotContactUntil ?? ''} onChange={(e) => set('doNotContactUntil', e.target.value || null)} />
        </Field>

        <Field label="Notering leveranslista"><textarea value={form.deliveryNote ?? ''} onChange={(e) => set('deliveryNote', e.target.value)} /></Field>
        <Field label="Notering ringlista"><textarea value={form.ringNote ?? ''} onChange={(e) => set('ringNote', e.target.value)} /></Field>

        <Field label="Vill ha ekologiskt">
          <TriSelect value={form.organicPreference} onChange={(v) => set('organicPreference', v)} />
        </Field>
        <Field label="Faktura via e-post">
          <TriSelect value={form.invoiceByEmail} onChange={(v) => set('invoiceByEmail', v)} />
        </Field>

        <Field label="Vill ha beställningsmejl">
          <input type="checkbox" checked={form.wantsOrderEmail} onChange={(e) => set('wantsOrderEmail', e.target.checked)} />
        </Field>
        <Field label="Aktiv">
          <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
        </Field>
        <Field label="Ingen SMS-avisering">
          <input type="checkbox" checked={form.smsOptOut} onChange={(e) => set('smsOptOut', e.target.checked)} />
        </Field>
        <Field label="Ingen webborder">
          <input type="checkbox" checked={form.onlineOrderOptOut} onChange={(e) => set('onlineOrderOptOut', e.target.checked)} />
        </Field>
      </div>

      {!isNew && (
        <div className="muted" style={{ marginTop: '0.5rem' }}>
          <div>
            Mobilnummer:{' '}
            {phoneVerifiedUtc
              ? <span className="badge">✓ Verifierat {new Date(phoneVerifiedUtc).toLocaleDateString('sv-SE')}</span>
              : 'ej verifierat'}
            {phoneChangedUtc && <> · senast bytt av kund {new Date(phoneChangedUtc).toLocaleDateString('sv-SE')}</>}
          </div>
          <div style={{ marginTop: '0.35rem' }}>Ett nummer du anger i fältet ovan sparas direkt, utan verifiering.</div>
          {isAdmin && (
            <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={() => void resetPhoneVerification(false)}>Återställ verifiering</button>
              <button onClick={() => void resetPhoneVerification(true)}>Rensa nummer &amp; återställ</button>
            </div>
          )}

          <div style={{ marginTop: '0.75rem' }}>
            E-post:{' '}
            {emailVerifiedUtc
              ? <span className="badge">✓ Verifierat {new Date(emailVerifiedUtc).toLocaleDateString('sv-SE')}</span>
              : 'ej verifierat'}
            {isAdmin && emailVerifiedUtc && (
              <button style={{ marginLeft: '0.5rem' }} onClick={() => void resetEmailVerification()}>
                Återställ verifiering
              </button>
            )}
          </div>
        </div>
      )}

      {accessKey && <p className="muted">AccessKey (orderlänk): <code>{accessKey}</code></p>}
      {accessKey && (
        <p className="muted">
          Länk för kundens egna uppgifter:{' '}
          <a href={`${window.location.origin}/min-sida?key=${encodeURIComponent(accessKey)}`}
            target="_blank" rel="noreferrer">
            {window.location.origin}/min-sida?key={accessKey}
          </a>
        </p>
      )}

      <div className="form-actions">
        <button className="primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Sparar…' : 'Spara'}
        </button>
        {!isNew && <button onClick={() => void remove()}>Radera</button>}
      </div>
    </>
  )

  const ordersTab = (
    <section className="card">
      <div className="page-head">
        <h2>Ordrar</h2>
        <button className="primary" onClick={() => navigate(`/orders/new?customerId=${customerId}`)}>Lägg order</button>
      </div>
          <table className="grid">
            <thead><tr><th>Order</th><th>Leveransdatum</th><th>Rader</th><th>Summa</th><th>Status</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="clickable" onClick={() => navigate(`/orders/${o.id}`)}>
                  <td>#{o.id}</td>
                  <td>{o.occasionDate}</td>
                  <td>{o.rowCount}</td>
                  <td>{o.total.toFixed(2)} kr</td>
                  <td><span className="badge">{ORDER_STATUS_LABEL[o.status]}</span></td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="muted">Inga ordrar.</td></tr>}
            </tbody>
          </table>
    </section>
  )

  const ledgerTab = ledger ? (
    <section className="card">
          <div className="page-head">
            <h2>Reskontra</h2>
            <span className={ledger.balance > 0 ? 'badge muted-badge' : 'badge'}>
              Saldo: {ledger.balance.toFixed(2)} kr {ledger.balance > 0 ? '(skuld)' : ledger.balance < 0 ? '(tillgodo)' : ''}
            </span>
          </div>

          <h3>Fakturor</h3>
          <table className="grid">
            <thead><tr><th>Faktura</th><th>Datum</th><th>Förfaller</th><th>Summa</th><th>Kvar</th><th>Status</th></tr></thead>
            <tbody>
              {ledger.invoices.map((i) => (
                <tr key={i.id} className="clickable" onClick={() => navigate(`/invoices/${i.id}`)}>
                  <td>#{i.id}</td><td>{i.invoiceDate}</td><td>{i.dueDate}</td>
                  <td>{i.total.toFixed(2)} kr</td><td>{i.remaining.toFixed(2)} kr</td>
                  <td><span className="badge">{INVOICE_STATUS_LABEL[i.status]}</span></td>
                </tr>
              ))}
              {ledger.invoices.length === 0 && <tr><td colSpan={6} className="muted">Inga fakturor.</td></tr>}
            </tbody>
          </table>

          <h3 style={{ marginTop: '1rem' }}>Betalningar / justeringar</h3>
          <table className="grid">
            <thead><tr><th>Datum</th><th>Typ</th><th>Belopp</th><th>Faktura</th><th>Kommentar</th></tr></thead>
            <tbody>
              {ledger.payments.map((p) => (
                <tr key={p.id}>
                  <td>{p.date}</td>
                  <td>{p.type === 'ManualPayment' ? 'Inbetalning' : 'Saldojustering'}</td>
                  <td>{p.amount.toFixed(2)} kr</td>
                  <td>{p.invoiceId ? `#${p.invoiceId}` : '—'}</td>
                  <td>{p.comment ?? ''}</td>
                </tr>
              ))}
              {ledger.payments.length === 0 && <tr><td colSpan={5} className="muted">Inga betalningar.</td></tr>}
            </tbody>
          </table>

          <h3 style={{ marginTop: '1rem' }}>Registrera betalning / justering</h3>
          <div className="row-form">
            <select value={pay.type} onChange={(e) => setPay({ ...pay, type: e.target.value as PaymentType })}>
              <option value="ManualPayment">Inbetalning</option>
              <option value="ManualAdjustment">Saldojustering</option>
            </select>
            <input type="number" step="0.01" placeholder="Belopp" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} style={{ width: '7rem' }} />
            <input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} />
            <input placeholder="Faktura-id (valfritt)" value={pay.invoiceId} onChange={(e) => setPay({ ...pay, invoiceId: e.target.value })} style={{ width: '9rem' }} />
            <input placeholder="Kommentar" value={pay.comment} onChange={(e) => setPay({ ...pay, comment: e.target.value })} />
            <button className="primary" onClick={() => void registerPayment()}>Registrera</button>
          </div>
    </section>
  ) : (
    <p className="muted">Laddar…</p>
  )

  const tabs: TabDef[] = [{ id: 'details', label: 'Uppgifter', content: detailsTab }]
  if (!isNew) {
    tabs.push({ id: 'orders', label: 'Ordrar', content: ordersTab })
    tabs.push({ id: 'ledger', label: 'Reskontra', content: ledgerTab })
  }

  return (
    <div>
      <div className="page-head">
        <h1>{isNew ? 'Ny kund' : `${form.firstName} ${form.lastName ?? ''}`}</h1>
        <button onClick={() => navigate('/customers')}>Tillbaka</button>
      </div>
      {error && <p className="error">{error}</p>}
      <Tabs tabs={tabs} />
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  )
}

function TriSelect({ value, onChange }: { value: TriState; onChange: (v: TriState) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as TriState)}>
      <option value="Unknown">Vet ej</option>
      <option value="Yes">Ja</option>
      <option value="No">Nej</option>
    </select>
  )
}

const describe = apiErrorMessage
